'use client';

import {
  type OpenChangeReason,
  autoUpdate,
  flip,
  hide,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStatus,
  useTypeahead,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCallbackRef } from '../../../hooks/use-callback-ref';
import { useControllableValue } from '../../../hooks/use-controllable-value';
import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import { useViewportLeaveClose } from '../../../hooks/use-viewport-leave-close';
import { OVERLAY_TRANSITION_DURATION_MS } from '../../../utils/motion';
import type {
  ComboboxOptionsType,
  ComboboxRootPropsInterface,
  ComboboxValueChangeReasonType,
  ComboboxValueType,
} from '../types/combobox-root.types';
import type {
  ComboboxFloatingContextInterface,
  UseComboboxReturnInterface,
} from '../types/use-combobox.types';
import { getComboboxLiveAnnouncement } from '../utils/get-combobox-live-announcement';
import { updateSelectedValues } from '../utils/update-selected-values';
import { useComboboxFilteredOptions } from './use-combobox-filtered-options';
import { useComboboxOptionUtils } from './use-combobox-option-utils';
import { useComboboxOptionsMap } from './use-combobox-options-map';
import { useHighlightPattern } from './use-highlight-pattern';

const DEFAULT_OPTIONS: ComboboxOptionsType = [];

/**
 * A comprehensive combobox hook that handles selection, filtering, keyboard navigation,
 * and floating UI positioning with support for both single and multi-select modes.
 *
 * @template ValueType - The combobox value type (string or Array<string>)
 * @param props - Configuration options for the combobox
 * @returns Object containing state and handlers for building a combobox
 *
 * TODO:
 * - Figure out scroll to selected option in single-select mode
 * - https://github.com/floating-ui/floating-ui/pull/3246 Prevent `FloatingFocusManager` from adding
 *  `aria-hidden` and `data-floating-ui-inert` to non-input nodes.
 */
export const useCombobox = <ValueType extends ComboboxValueType>(
  props: Partial<ComboboxRootPropsInterface<ValueType>>,
): {
  combobox: UseComboboxReturnInterface;
  floating: ComboboxFloatingContextInterface;
} => {
  const {
    // Floating UI open state management props
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange: setControlledOpen,

    // Floating UI positioning props
    offset: offsetValue = 8,
    overflowPadding = 8,
    placement = 'bottom-start',
    shouldCloseOnViewportLeave = false,
    shouldAwaitInteractionResponse = true,

    // Floating UI hook props
    useDismissProps,
    useRoleProps,

    // Combobox value state management props
    defaultValue,
    value: controlledValue,
    onChange: setControlledValue,

    // Combobox behavior props
    allowSubmitWhileOpen = false,
    autoCompleteBehavior = 'automatic',
    fluidDropdown = true,
    focusFirstOption = false,
    freeForm = false,
    loading = false,
    editable = true,
    readOnly = false,
    getHighlightPattern,
    selectCallbackFrequency = 'change',
    showLabelForValue = false,

    // Combobox options management props
    options = DEFAULT_OPTIONS,
    optionFilter,

    // Combobox callbacks
    onChipClose: onChipCloseCallback,
    onClear: onClearCallback,
    onOptionFocus: onOptionFocusCallback,
    onOptionSelect: onOptionSelectCallback,
  } = props;

  const instructionsId = `c11n-combobox-instructions-${useId()}`;

  /**
   * DOM references for positioning and navigation
   */
  const elementsRef = useRef<Array<HTMLDivElement> | Array<null>>([]);
  const labelsRef = useRef<Array<string> | Array<null>>([]);
  const focusOutAnimationFrameIdsRef = useRef<Set<number>>(new Set());

  /**
   * Open state management (controlled or uncontrolled)
   */
  const isControlledOpen = 'open' in props;
  const [open, setOpen] = useControllableValue<
    boolean | undefined,
    [event?: Event, reason?: OpenChangeReason]
  >({
    controlledValue: controlledOpen,
    uncontrolledValue: defaultOpen,
    isControlled: isControlledOpen,
    isUncontrolled: 'defaultOpen' in props,
    onChange: (newOpen, event, reason) => {
      if (newOpen !== undefined) {
        setControlledOpen?.(newOpen, event, reason);
      }
    },
    componentName: 'Combobox.Root',
    controlledPropName: 'open',
    uncontrolledPropName: 'defaultOpen',
  });

  /**
   * Value state management (controlled or uncontrolled)
   */
  const isControlledValue = 'value' in props;
  const [valueState, setValueState] = useControllableValue<ValueType | undefined>({
    controlledValue: controlledValue,
    uncontrolledValue: defaultValue,
    isControlled: isControlledValue,
    isUncontrolled: 'defaultValue' in props,
    componentName: 'Combobox.Root',
    controlledPropName: 'value',
    uncontrolledPropName: 'defaultValue',
  });
  const value = (isControlledValue ? valueState : valueState || '') as ValueType;
  const isMultiSelect = Array.isArray(value);
  const prevValueRef = useRef(value);

  /**
   * Set of selected values for efficient lookups and operations
   * Using a Set for O(1) access when checking if a value is selected
   */
  const [selectedValues, setSelectedValues] = useState<Set<string>>(
    () => new Set(Array.isArray(value) ? value : [value].filter(Boolean)),
  );

  /**
   * Sync selectedValues when controlled value changes
   */
  useEffect(() => {
    if (isControlledValue) {
      // oxlint-disable-next-line react/set-state-in-effect -- The lookup set mirrors externally controlled selection.
      setSelectedValues(new Set(Array.isArray(value) ? value : [value].filter(Boolean)));
    }
  }, [isControlledValue, value]);

  /**
   * Map of option values to their full option objects
   */
  const optionsMap = useComboboxOptionsMap(options);

  const getReadOnlyMultiSelectInputValue = useCallback(
    (values: Array<string>): string =>
      values
        .map((selectedValue) => {
          const option = optionsMap.get(selectedValue);

          if (!option) {
            return freeForm ? selectedValue : null;
          }

          if (typeof option === 'object') {
            return showLabelForValue ? option.label : option.value;
          }

          return option;
        })
        .filter((displayValue): displayValue is string => Boolean(displayValue))
        .join(', '),
    [freeForm, optionsMap, showLabelForValue],
  );

  /**
   * Calculate initial input value
   */
  const initialInputValue = (): string => {
    if (Array.isArray(value)) {
      return readOnly ? getReadOnlyMultiSelectInputValue(value) : '';
    }

    if (!value) {
      return '';
    }

    if (showLabelForValue) {
      const option = optionsMap.get(value);

      if (option) {
        return typeof option === 'object' ? option.label : option;
      }
    }

    return value;
  };

  /**
   * Input state for tracking user interactions
   * - inputValue: Current text in the input field
   * - isDirty: Whether the input has been modified by user
   * - activeIndex: Currently highlighted option in the dropdown
   */
  const [inputValue, setInputValue] = useState(initialInputValue());
  const [isDirty, setIsDirty] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);

  /**
   * Option management for filtering and selection
   * - filteredOptions: Options filtered based on input value
   * - highlightPattern: Function to highlight filtered options
   * - getOptionByIndex: Function to retrieve option at a specific index
   * - getValueByOption: Function to retrieve option value
   */
  const filteredOptions = useComboboxFilteredOptions({
    autoCompleteBehavior,
    isDirty,
    optionFilter,
    options,
    value: inputValue,
  });
  const liveAnnouncement = getComboboxLiveAnnouncement({
    autoCompleteBehavior,
    filteredOptions,
    isDirty,
    loading,
    open,
  });

  const highlightPattern = useHighlightPattern({
    autoCompleteBehavior,
    isDirty,
    inputValue,
    getHighlightPattern,
  });

  const { getOptionByIndex, getValueByOption, getIndexByValue } = useComboboxOptionUtils({
    options: filteredOptions,
  });

  /**
   * Sync inputValue with controlled value changes from parent component.
   * Normally skipped while the user is actively editing (isDirty), but an
   * external/programmatic value change always takes precedence.
   */
  useEffect(() => {
    const prev = prevValueRef.current;
    const valueChanged = isMultiSelect
      ? !Array.isArray(prev) ||
        prev.length !== (value as Array<string>).length ||
        prev.some((v, i) => v !== (value as Array<string>)[i])
      : prev !== value;
    prevValueRef.current = value;

    const shouldSync = !isDirty || valueChanged;

    if (isControlledValue && shouldSync && !isMultiSelect) {
      // Handle empty value case first
      if (!value) {
        // oxlint-disable-next-line react/set-state-in-effect -- Input text mirrors the externally controlled selection.
        setInputValue('');
        return;
      }

      // Try to find the value in options
      const option = optionsMap.get(value as string);

      if (freeForm) {
        // Freeform mode: show label if option exists and showLabelForValue is true, otherwise show value as-is
        if (option && showLabelForValue) {
          setInputValue(typeof option === 'object' ? option.label : option);
        } else {
          setInputValue(value as string);
        }
      } else {
        // Non-freeform mode: only show value if it exists in options
        if (option) {
          if (showLabelForValue) {
            setInputValue(typeof option === 'object' ? option.label : option);
          } else {
            setInputValue(value as string);
          }
        } else {
          // Value doesn't exist in options and freeform is false - clear input
          setInputValue('');
        }
      }
    }

    // Multi-select: keep input text in sync for both controlled and uncontrolled modes
    if (shouldSync && isMultiSelect) {
      setInputValue(readOnly ? getReadOnlyMultiSelectInputValue(value as Array<string>) : '');
    }
  }, [
    freeForm,
    getReadOnlyMultiSelectInputValue,
    isControlledValue,
    isDirty,
    isMultiSelect,
    readOnly,
    showLabelForValue,
    optionsMap,
    value,
  ]);

  /**
   * Sync selectedIndex and activeIndex if value is present
   * Only set activeIndex if user hasn't started navigating with keyboard
   */
  useEffect(() => {
    // Early return if no value
    if (value) {
      // Get index by selected option
      const index = getIndexByValue(Array.isArray(value) ? value[0] : value);

      // If option exists, set selected index and activeIndex (if user hasn't navigated)
      if (index >= 0) {
        // oxlint-disable-next-line react/set-state-in-effect -- Selection changes reset the keyboard cursor.
        setSelectedIndex(index);
        // Only set activeIndex if user hasn't started keyboard navigation
        if (!hasUserNavigated) {
          setActiveIndex(index);
        }
      }
    }
  }, [value, getIndexByValue, hasUserNavigated]);

  /**
   * Reset navigation flag when dropdown opens/closes
   */
  useEffect(() => {
    if (!open) {
      // oxlint-disable-next-line react/set-state-in-effect -- Closing ends the keyboard-navigation session.
      setHasUserNavigated(false);
    }
  }, [open]);

  /**
   * Auto select first option while actively filtering results in automatic auto complete behavior
   */
  useEffect(() => {
    if (isDirty && autoCompleteBehavior === 'automatic') {
      // oxlint-disable-next-line react/set-state-in-effect -- Filter changes drive automatic highlighting.
      setActiveIndex(inputValue.trim() && filteredOptions.length ? 0 : null);
    }
  }, [isDirty, autoCompleteBehavior, inputValue, filteredOptions]);

  const floatingMiddleware = useMemo(
    () => [
      offset(offsetValue),
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
        padding: overflowPadding,
      }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: fluidDropdown ? `${rects.reference.width}px` : undefined,
          });
        },
        padding: overflowPadding,
      }),
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
    [offsetValue, overflowPadding, fluidDropdown, shouldCloseOnViewportLeave],
  );

  /**
   * Floating UI setup for dropdown positioning
   * Handles dropdown placement, flipping, sizing, and visibility
   */
  const { context, refs, floatingStyles, middlewareData, isPositioned } = useFloating({
    placement,
    open,
    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event as unknown as SyntheticEvent, reason);
    },
    whileElementsMounted: autoUpdate,
    middleware: floatingMiddleware,
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up dismiss and list keyboard navigation, and ARIA attributes based on role
   */
  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) =>
      !(event.target as HTMLElement).closest('[data-c11n-component="Combobox.Trigger"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'listbox',
    ...useRoleProps,
  });

  const typeahead = useTypeahead(context, {
    enabled: autoCompleteBehavior === 'none' && !editable && !readOnly,
    listRef: labelsRef,
    activeIndex,
    selectedIndex: isMultiSelect ? undefined : selectedIndex,
    onMatch: setActiveIndex,
  });

  const listNav = useListNavigation(context, {
    enabled: !readOnly,
    listRef: elementsRef,
    activeIndex,
    selectedIndex: isMultiSelect ? undefined : selectedIndex,
    onNavigate: (index) => {
      setActiveIndex(index);
      // Mark that user has started navigating
      setHasUserNavigated(true);
    },
    focusItemOnOpen: focusFirstOption,
    // Loops virtual focus across the list
    loop: true,
    // Keeps focus on the input and uses virtual focus via `aria-activedescendant`
    virtual: true,
  });

  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([
    dismiss,
    role,
    typeahead,
    listNav,
  ]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, status: transitionStatus } = useTransitionStatus(context, {
    duration: reducedMotion ? 0 : OVERLAY_TRANSITION_DURATION_MS,
  });

  /**
   * Programmatically focus on input
   */
  const focusOnInput = useCallback(() => {
    (refs.domReference.current as HTMLInputElement)?.focus();
  }, [refs]);

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newOpen - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newOpen: boolean, event?: SyntheticEvent, reason?: OpenChangeReason) => {
      setOpen(newOpen, event as unknown as Event, reason);
    },
    [setOpen],
  );

  useViewportLeaveClose({
    enabled: shouldCloseOnViewportLeave,
    isPositioned,
    middlewareData,
    onOpenChange: updateOpenState,
    open,
  });

  /**
   * Handles controlled or uncontrolled "open" state
   *
   * @param newOpen - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateValueState = useCallback(
    (newValue: ValueType, event: SyntheticEvent, reason: ComboboxValueChangeReasonType) => {
      if (!isControlledValue) {
        setValueState(newValue);
      }

      setControlledValue?.(newValue, event, reason);
    },
    [isControlledValue, setControlledValue, setValueState],
  );

  /**
   * Handles props.onOptionFocus callback
   *
   * @param optionIndex - Active option index
   */
  const onOptionFocus = useCallback(
    (optionIndex: number): void => {
      // Early return if props.onOptionFocus not provided
      if (!onOptionFocusCallback) {
        return;
      }

      // Get option by active index
      const option = getOptionByIndex(optionIndex);

      // Trigger callback
      if (option) {
        onOptionFocusCallback(option);
      }
    },
    [onOptionFocusCallback, getOptionByIndex],
  );

  useEffect(() => {
    if (activeIndex !== null) {
      onOptionFocus(activeIndex);
    }
  }, [activeIndex, onOptionFocus]);

  /**
   * Handles props.onOptionSelect callback
   *
   * @param optionValue - New selected Value
   */
  const onOptionSelect = useCallback(
    (optionValue: string): void => {
      // Early return if props.onOptionSelect not provided
      if (!onOptionSelectCallback) {
        return;
      }

      // Get selected option by value
      const option = optionsMap.get(optionValue);

      // Early return if there is no selected option
      if (!option) {
        return;
      }

      // Trigger callback every time
      if (selectCallbackFrequency === 'always' || isMultiSelect) {
        onOptionSelectCallback(option);
      }

      // Trigger callback only when selected value is new
      if (selectCallbackFrequency === 'change' && !selectedValues.has(optionValue)) {
        onOptionSelectCallback(option);
      }
    },
    [optionsMap, onOptionSelectCallback, isMultiSelect, selectedValues, selectCallbackFrequency],
  );

  /**
   * Handles Combobox.Trigger click action
   *
   * @param event - Event object
   */
  const onClickTrigger = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => {
      if (readOnly) {
        focusOnInput();
        return;
      }

      // Open dropdown if not already open
      if (!open) {
        updateOpenState(true, event, 'click');
      }

      // Focus on input
      focusOnInput();
    },
    [focusOnInput, open, readOnly, updateOpenState],
  );

  /**
   * Handles Combobox.Clear button action
   *
   * @param event - Event object
   */
  const onClickClear = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      if (readOnly) {
        return;
      }

      // Get new selected values state
      const newSelectedValues = updateSelectedValues('', selectedValues, 'clear');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState((isMultiSelect ? [] : '') as ValueType, event, 'clear');

      // Reset input value
      setInputValue('');

      // Reset dirty state
      setIsDirty(false);

      // Re-focus on input
      focusOnInput();

      // Trigger props.onClear callback if exists
      onClearCallback?.(event);
    },
    [focusOnInput, isMultiSelect, onClearCallback, readOnly, selectedValues, updateValueState],
  );

  /**
   * Handles InputChip.Close button action
   *
   * @param optionValue - Value from option or option.value
   * @param event - Event object
   */
  const onClickInputChipClose = useCallback(
    (optionValue: string, event: SyntheticEvent<HTMLSpanElement>) => {
      if (readOnly) {
        return;
      }

      // Get new selected values state
      const newSelectedValues = updateSelectedValues(optionValue, selectedValues, 'remove');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState(Array.from(newSelectedValues) as ValueType, event, 'remove');

      // Reset input value
      setInputValue('');

      // Reset dirty state
      setIsDirty(false);

      // Re-focus on input
      focusOnInput();

      // Trigger props.onChipCloseCallback if exists
      onChipCloseCallback?.(optionValue, event);
    },
    [focusOnInput, onChipCloseCallback, readOnly, selectedValues, updateValueState],
  );

  /**
   * Handles input blur for multi select
   *
   * @param event - Event object
   */
  const onBlurInputMultiSelect = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      // Get input value and trim it
      const newValue = inputValue.trim();

      // Update state only when prop.freeForm is true and newValue is not empty
      if (freeForm && newValue !== '') {
        // Get new selected values state
        const newSelectedValues = updateSelectedValues(newValue, selectedValues, 'toggle');

        // Update selectedValues with new state
        setSelectedValues(newSelectedValues);

        // Trigger controlled or uncontrolled value state change
        updateValueState(Array.from(newSelectedValues) as ValueType, event, 'toggle');
      }

      // Reset input value
      setInputValue('');

      // Reset dirty state
      setIsDirty(false);
    },
    [inputValue, freeForm, selectedValues, updateValueState],
  );

  /**
   * Handles input blur for single select
   *
   * @param event - Event object
   */
  const onBlurInputSingleSelect = useCallback(() => {
    // Get input value and trim it
    const newValue = inputValue.trim();

    // Early return if props.freeForm is true, because value is auto updated on every key down
    // Or when input value is empty
    if (freeForm || newValue === '') {
      // Reset dirty state
      setIsDirty(false);
      return;
    }

    // Get selected value from selectedValues
    const optionValue = Array.from(selectedValues)[0];

    // Early return if no value was selected
    if (!optionValue) {
      return;
    }

    // Get selected option by value
    const option = optionsMap.get(optionValue);

    // Early return if no option was selected
    // Note: This is not possible, but still handling it
    if (!option) {
      return;
    }

    // Get label from selected option, either from key or option itself
    const label = typeof option === 'object' ? option.label : option;

    // Set input to label or value based on props.showLabelForValue
    setInputValue(showLabelForValue ? label : optionValue);

    // Reset dirty state
    setIsDirty(false);
  }, [inputValue, freeForm, selectedValues, showLabelForValue, optionsMap]);

  /**
   * Handles input blur for multi or single select
   *
   * @param event - Event object
   */
  const onBlurInput = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (readOnly) {
        setIsDirty(false);
        return;
      }

      if (isMultiSelect) {
        onBlurInputMultiSelect(event);
      } else {
        onBlurInputSingleSelect();
      }
    },
    [isMultiSelect, onBlurInputMultiSelect, onBlurInputSingleSelect, readOnly],
  );

  /**
   * Handles input "enter" key down for multi select
   *
   * @param optionValue - Option value
   * @param event - Event object
   */
  const onKeyDownInputEnterMultiSelect = useCallback(
    (optionValue: string, event: KeyboardEvent<HTMLInputElement>) => {
      // Get new selected values state
      const newSelectedValues = updateSelectedValues(optionValue, selectedValues, 'toggle');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState(Array.from(newSelectedValues) as ValueType, event, 'toggle');

      // Reset input value
      setInputValue('');

      // Reset dirty state
      setIsDirty(false);

      // Trigger props.onOptionSelect via callback if it exists
      onOptionSelect(optionValue);
    },
    [selectedValues, updateValueState, onOptionSelect],
  );

  /**
   * Handles input "enter" key down for single select
   *
   * @param event - Event object
   */
  const onKeyDownInputEnterSingleSelect = useCallback(
    (optionValue: string, event: KeyboardEvent<HTMLInputElement>) => {
      // Early return if there is no activeIndex and props.freeForm is true,
      // because value is auto updated on every key down
      if (activeIndex === null && freeForm) {
        // Reset dirty state
        setIsDirty(false);
        // Close dropdown
        updateOpenState(false, event, 'reference-press');
        return;
      }

      // Get selected option by value
      const option = optionsMap.get(optionValue);

      // Early return if no option was selected
      // Note: This is not possible, but still handling it
      if (!option) {
        return;
      }

      // Get new selected values state
      const newSelectedValues = updateSelectedValues(optionValue, selectedValues, 'replace');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState(optionValue as ValueType, event, 'replace');

      // Get label from selected option, either from key or option itself
      const label = typeof option === 'object' ? option.label : option;

      // Set input to label or value based on props.showLabelForValue
      setInputValue(showLabelForValue ? label : optionValue);

      // Reset dirty state
      setIsDirty(false);

      // Close dropdown
      updateOpenState(false, event, 'reference-press');

      // Trigger props.onOptionSelect via callback if it exists
      onOptionSelect(optionValue);
    },
    [
      activeIndex,
      selectedValues,
      updateValueState,
      onOptionSelect,
      showLabelForValue,
      updateOpenState,
      freeForm,
      optionsMap,
    ],
  );

  /**
   * Handles input "enter" key down for multi and single select
   *
   * @param event - Event object
   */
  const onKeyDownInputEnter = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (readOnly) {
        return;
      }

      // Prevent form submission when props.allowSubmitWhileOpen is false and dropdown is open
      if (!allowSubmitWhileOpen && open) {
        event.preventDefault();
      }

      // Early return if there is no activeIndex and props.freeForm is false
      if (activeIndex === null && !freeForm && selectedValues.size === 0) {
        return;
      }

      let optionValue: string | null = null;

      // Get option by activeIndex
      const option = getOptionByIndex(activeIndex);

      // If action option exists, or props.freeForm is true
      if (option) {
        // Get active option value
        optionValue = getValueByOption(option);
      } else if (freeForm) {
        // Get trimmed input value
        optionValue = inputValue.trim();
      } else {
        // Get currently selected option
        optionValue = Array.from(selectedValues)[0];
      }

      // Early return if there is option value
      if (!optionValue) {
        return;
      }

      // Handle multi or single select updates
      if (isMultiSelect) {
        onKeyDownInputEnterMultiSelect(optionValue, event);
      } else {
        onKeyDownInputEnterSingleSelect(optionValue, event);
      }
    },
    [
      allowSubmitWhileOpen,
      open,
      isMultiSelect,
      activeIndex,
      freeForm,
      inputValue,
      getOptionByIndex,
      onKeyDownInputEnterMultiSelect,
      onKeyDownInputEnterSingleSelect,
      getValueByOption,
      selectedValues,
      readOnly,
    ],
  );

  /**
   * Handles input "backspace" key down for multi select
   *
   * @param event - Event object
   */
  const onKeyDownInputBackspaceMultiSelect = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (readOnly) {
        return;
      }

      // Early return if input has value or if selected values set is empty
      if (event.currentTarget.value !== '' || selectedValues.size === 0) {
        return;
      }

      // Prevent default event behavior
      event.preventDefault();

      // Get the last selected value
      const optionValue = Array.from(selectedValues)[selectedValues.size - 1];

      // Get new selected values state
      const newSelectedValues = updateSelectedValues(optionValue, selectedValues, 'remove');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState(Array.from(newSelectedValues) as ValueType, event, 'remove');
    },
    [readOnly, selectedValues, updateValueState],
  );

  /**
   * Handles input "backspace" key down for multi select
   *
   * @param event - Event object
   */
  const onKeyDownInputBackspace = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isMultiSelect) {
        onKeyDownInputBackspaceMultiSelect(event);
      }
    },
    [isMultiSelect, onKeyDownInputBackspaceMultiSelect],
  );

  /**
   * Handles input key down for "enter" and "backspace" keys.
   *
   * Wrapped in `useCallbackRef` so the returned handler keeps a stable
   * identity across renders — otherwise the inner dep chain
   * (`onKeyDownInputEnter`, `onKeyDownInputBackspace`, `updateOpenState`)
   * destabilizes `useCombobox`'s memoized return on every render.
   */
  const onKeyDownInput = useCallbackRef((event: KeyboardEvent<HTMLInputElement>) => {
    if (readOnly) {
      return;
    }

    switch (event.code) {
      case 'Enter': {
        if (!open) {
          const input = event.currentTarget;
          const isInForm = input?.closest?.('form') !== null;

          if (!isInForm) {
            updateOpenState(true, event, 'reference-press');
          }
        } else {
          onKeyDownInputEnter(event);
        }
        break;
      }
      case 'Tab': {
        updateOpenState(false, event, 'focus-out');
        break;
      }
      case 'Backspace':
      case 'Delete': {
        onKeyDownInputBackspace(event);
        break;
      }
    }
  });

  /**
   * Handles input change for multi select
   *
   * @param newValue - Input value
   * @param event - Event object
   */
  const onChangeInputMultiSelect = useCallback((newValue: string) => {
    // Update input value state
    setInputValue(newValue);
  }, []);

  /**
   * Handles input change for single select
   *
   * @param newValue - Input value
   * @param event - Event object
   */
  const onChangeInputSingleSelect = useCallback(
    (newValue: string, event: ChangeEvent<HTMLInputElement>) => {
      // Update input value
      setInputValue(newValue);

      // Update state only when prop.freeForm is true
      if (freeForm) {
        // Get new selected values state
        const newSelectedValues = updateSelectedValues(
          newValue,
          selectedValues,
          newValue === '' ? 'clear' : 'replace',
        );

        // Update selectedValues with new state
        setSelectedValues(newSelectedValues);

        // Update input value
        setInputValue(newValue);

        // Trigger controlled or uncontrolled value state change
        updateValueState(newValue as ValueType, event, newValue === '' ? 'clear' : 'replace');
      }
    },
    [freeForm, selectedValues, updateValueState],
  );

  /**
   * Handles input change for single and multi select
   *
   * @param event - Event object
   */
  const onChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (readOnly) {
        return;
      }

      // Set dirty state
      setIsDirty(true);

      // Open dropdown
      updateOpenState(true, event, 'click');

      if (isMultiSelect) {
        onChangeInputMultiSelect(event.target.value);
      } else {
        onChangeInputSingleSelect(event.target.value, event);
      }
    },
    [isMultiSelect, onChangeInputMultiSelect, onChangeInputSingleSelect, readOnly, updateOpenState],
  );

  /**
   * Handles option click for multi select
   *
   * @param optionValue - Option value
   * @param event - Event object
   */
  const onClickOptionMultiSelect = useCallback(
    (optionValue: string, event: SyntheticEvent<HTMLDivElement>) => {
      // Get new selected values state
      const newSelectedValues = updateSelectedValues(optionValue, selectedValues, 'toggle');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState(Array.from(newSelectedValues) as ValueType, event, 'toggle');

      // Reset input value
      setInputValue('');

      // Reset dirty state
      setIsDirty(false);

      // Re-focus on input
      focusOnInput();

      // Trigger props.onOptionSelect via callback if it exists
      onOptionSelect(optionValue);
    },
    [selectedValues, onOptionSelect, updateValueState, focusOnInput],
  );

  /**
   * Handles option click for single select
   *
   * @param optionValue - Option value
   * @param event - Event object
   */
  const onClickOptionSingleSelect = useCallback(
    (optionValue: string, event: SyntheticEvent<HTMLDivElement>) => {
      // Get selected option by value
      const option = optionsMap.get(optionValue);

      // Early return if no option was selected
      // Note: This is not possible, but still handling it
      if (!option) {
        return;
      }

      // Get new selected values state
      const newSelectedValues = updateSelectedValues(optionValue, selectedValues, 'replace');

      // Update selectedValues with new state
      setSelectedValues(newSelectedValues);

      // Trigger controlled or uncontrolled value state change
      updateValueState(optionValue as ValueType, event, 'replace');

      // Get label from selected option, either from key or option itself
      const label = typeof option === 'object' ? option.label : option;

      // Set input to label or value based on props.showLabelForValue
      setInputValue(showLabelForValue ? label : optionValue);

      // Reset dirty state
      setIsDirty(false);

      // Close dropdown
      updateOpenState(false, event, 'click');

      // Re-focus on input
      focusOnInput();

      // Trigger props.onOptionSelect via callback if it exists
      onOptionSelect(optionValue);
    },
    [
      selectedValues,
      optionsMap,
      showLabelForValue,
      updateValueState,
      onOptionSelect,
      focusOnInput,
      updateOpenState,
    ],
  );

  /**
   * Handles option click for single and multi select
   *
   * @param newValue - Option value
   * @param optionIndex - Option index
   * @param event - Event object
   */
  const onClickOption = useCallback(
    (newValue: string, event: SyntheticEvent<HTMLDivElement>) => {
      if (readOnly) {
        return;
      }

      if (isMultiSelect) {
        onClickOptionMultiSelect(newValue, event);
      } else {
        onClickOptionSingleSelect(newValue, event);
      }
    },
    [isMultiSelect, onClickOptionMultiSelect, onClickOptionSingleSelect, readOnly],
  );

  /**
   * Effect to handle auto-scrolling to the selected option
   */
  useEffect(() => {
    if (isMounted && selectedIndex && selectedIndex !== -1) {
      const animationFrameId = requestAnimationFrame(() => {
        const selectedElement = elementsRef.current[selectedIndex];
        const container = selectedElement?.parentElement;

        if (selectedElement && container) {
          const elementOffset = selectedElement.offsetTop;

          setActiveIndex(selectedIndex);
          container.scrollTo({
            top: elementOffset,
          });
        }
      });

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [isMounted, selectedIndex]);

  /**
   * Effect to handle auto-closing the combobox dropdown when focus leaves the combobox container.
   *
   * This effect sets up a global focus tracking mechanism that detects when focus moves
   * outside of the Combobox.Trigger element and automatically closes the dropdown.
   *
   * The implementation uses requestAnimationFrame to ensure focus state is checked
   * after the browser has completed focus transitions, making it reliable in various
   * contexts including iframe environments like Storybook.
   */
  useEffect(() => {
    // Only run this effect when the combobox is open
    if (!open) {
      return;
    }

    const focusOutAnimationFrameIds = focusOutAnimationFrameIdsRef.current;

    /**
     * Handles focus leaving elements within the document.
     * Determines if focus has moved outside the combobox trigger and closes the dropdown if it has.
     */
    const handleFocusOut = (event: Event) => {
      // Get the trigger element by finding the closest parent from the input
      const inputElement = refs.domReference.current;
      const triggerElement = inputElement?.closest('[data-c11n-component="Combobox.Trigger"]');
      const dropdownElement = refs.floating.current;

      // Use requestAnimationFrame to defer the check until after the browser has updated focus
      const animationFrameId = requestAnimationFrame(() => {
        focusOutAnimationFrameIds.delete(animationFrameId);

        const activeElement = document.activeElement;

        // If focus moved to the dropdown container itself (scrollbar click), restore focus
        if (activeElement === dropdownElement) {
          (inputElement as HTMLElement)?.focus();
          return;
        }

        // If focus went to body, it's likely a scrollbar click - restore focus
        if (!activeElement || activeElement === document.body) {
          (inputElement as HTMLElement)?.focus();
          return;
        }

        // Check if focus is inside either the trigger OR the dropdown
        const isInsideTrigger = triggerElement?.contains(activeElement);
        const isInsideDropdown = dropdownElement?.contains(activeElement);

        if (!isInsideTrigger && !isInsideDropdown) {
          updateOpenState(false, event as unknown as SyntheticEvent, 'focus-out');
        }
      });

      focusOutAnimationFrameIds.add(animationFrameId);
    };

    // Listen for focusout at document level
    document.addEventListener('focusout', handleFocusOut);

    // Clean up the event listener when the component unmounts or dependencies change
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
      focusOutAnimationFrameIds.forEach((animationFrameId) => {
        cancelAnimationFrame(animationFrameId);
      });
      focusOutAnimationFrameIds.clear();
    };
  }, [open, refs.domReference, refs.floating, updateOpenState]);

  /*
   * The stable side of the return: every field here is either constant by
   * construction or memoized upstream, so the object identity only changes
   * when meaningful combobox state changes. Floating-ui-bridged fields
   * whose identity churns on every render (`context`, `getFloatingProps`)
   * are NOT here — they live on `floating` below and ride their own
   * provider so they don't destabilize the rest of the consumer subtree.
   */
  const combobox = useMemo(
    () => ({
      elementsRef,
      labelsRef,
      refs,

      activeIndex,
      open,
      isMounted,

      floatingStyles,
      transitionStatus,

      inputValue,
      instructionsId,
      listboxId: context.floatingId,
      liveAnnouncement,
      isMultiSelect,
      editable,
      selectedValues: Array.from(selectedValues),

      filteredOptions,
      optionsMap,
      highlightPattern,

      getItemProps,
      getReferenceProps,

      middlewareData,

      onChangeInput,
      onKeyDownInput,
      onBlurInput,
      onClickOption,
      onClickInputChipClose,
      onClickClear,
      onClickTrigger,
    }),
    [
      refs,
      activeIndex,
      open,
      isMounted,
      floatingStyles,
      transitionStatus,
      inputValue,
      instructionsId,
      context.floatingId,
      liveAnnouncement,
      isMultiSelect,
      editable,
      selectedValues,
      filteredOptions,
      optionsMap,
      highlightPattern,
      getItemProps,
      getReferenceProps,
      middlewareData,
      onChangeInput,
      onKeyDownInput,
      onBlurInput,
      onClickOption,
      onClickInputChipClose,
      onClickClear,
      onClickTrigger,
    ],
  );

  /*
   * Floating-ui-bridged data. Re-created every render by design — its
   * consumers (only `Combobox.Content`) need this-render-fresh values for
   * floating-ui to position correctly. Wired through a separate context so
   * only those consumers re-render.
   */
  const floating = { context, getFloatingProps };

  return { combobox, floating };
};
